import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { inventaireApi } from "./inventaireApi";
import { IconChevronLeft } from "./icons";
import { badgeLigne, formatDate } from "./statuts";
import "./inventaire.css";

export default function InventaireDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [page, setPage] = useState(0);
  const [size] = useState(20);

  const [totalElements, setTotalElements] = useState(0);
  const [isLast, setIsLast] = useState(true);

  // reset when id changes
  useEffect(() => {
    setLignes([]);
    setPage(0);
    setTotalElements(0);
    setIsLast(true);
    setLoading(true);

    loadPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPage = async (p, replace = false) => {
    try {
      replace ? setLoading(true) : setLoadingMore(true);

      const res = await inventaireApi.getLignesPage(id, p, size, "scanDate", "desc");

      const data = res.data || {};
      const content = data.content || [];

      setTotalElements(data.totalElements ?? 0);
      setIsLast(Boolean(data.last));     // ✅ best indicator
      setPage(data.number ?? p);         // ✅ actual page

      setLignes((prev) => (replace ? content : [...prev, ...content]));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const onLoadMore = () => {
    if (loadingMore || isLast) return;
    loadPage(page + 1, false);
  };

  const headerSubtitle = useMemo(() => {
    if (loading) return "Chargement...";
    return `${lignes.length} / ${totalElements} rouleaux`;
  }, [loading, lignes.length, totalElements]);

  return (
    <div>
      {/* HEADER */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <IconChevronLeft />
          Retour
        </button>

        <div>
          <div className="page-header-code">Inventaire #{id}</div>
          <div className="page-header-sub">{headerSubtitle}</div>
        </div>
      </div>

      {/* LOADING FIRST */}
      {loading && <div className="inv-loader">Chargement des rouleaux...</div>}

      {/* EMPTY */}
      {!loading && lignes.length === 0 && (
        <div className="inv-empty">
          <div className="empty-icon">📦</div>
          <h3>Aucune ligne</h3>
          <p>Aucun rouleau scanné pour cet inventaire.</p>
        </div>
      )}

      {/* LIST */}
      {!loading && lignes.length > 0 && (
        <div className="inv-body">
          <div className="inv-list" style={{ gap: 12 }}>
            {lignes.map((l) => {
              // Pas de `portee` passee : cet ecran ne charge que les LIGNES, pas l'en-tete de
              // l'inventaire. Le badge reste donc generique (« HORS PÉRIM. ») plutot que d'affirmer
              // « AUTRE IMPORT », faux sur un inventaire par tissu -- le motif exact est de toute
              // facon affiche juste en dessous, dans `commentaire`.
              const badge = badgeLigne(l.statut);

              return (
                <div key={l.id} className="inv-card" style={{ padding: 16 }}>
                  <div className="inv-card-head">
                    {/* numeroRouleau retombe deja sur numeroRouleauScan cote backend ; on garde le
                        champ brut en secours pour les lignes INCONNU. */}
                    <h3 className="mono">
                      Rouleau #{l.numeroRouleau || l.numeroRouleauScan}
                    </h3>
                    <span className={`pill pill--sm pill--${badge.variant}`}>
                      {badge.label}
                    </span>
                  </div>

                  <div className="inv-info-grid">
                    <div>
                      <span>Tissu</span>
                      <b>{l.tissu || "-"}</b>
                    </div>

                    <div>
                      <span>N° Import</span>
                      <b>{l.numeroImport || "-"}</b>
                    </div>

                    <div>
                      <span>Scanné par</span>
                      <b>{l.scannedBy || "-"}</b>
                    </div>

                    <div>
                      <span>Date Scan</span>
                      <b>{formatDate(l.scanDate)}</b>
                    </div>
                  </div>

                  {/* "Appartient a l'import X", "Rescanne", "Recupere manuellement"... */}
                  {l.commentaire && <div className="inv-note">{l.commentaire}</div>}
                </div>
              );
            })}
          </div>

          {/* LOAD MORE */}
          <button
            className="btn btn-neutral"
            style={{ marginTop: 12 }}
            onClick={onLoadMore}
            disabled={isLast || loadingMore}
          >
            {loadingMore ? "Chargement..." : isLast ? "Fin de la liste" : "Charger plus"}
          </button>
        </div>
      )}
    </div>
  );
}
